import { type metrics } from "@north-beam/nb-common";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import {
  useCustomOnConfirmModal,
  useCustomOnConfirmSwitchMetricModal,
  useHasSubmittedMetric,
  useSelectedIsDirtyMetric,
} from "./atoms";
import { useCurrentlyUpdatingMetric } from "./hooks/use-currently-updating-metric";
import { useCustomMetrics } from "./hooks/use-custom-metrics";
import { useFormulaHistory } from "./hooks/use-formula-history";

export const ConfirmModal = ({
  onConfirm,
  onCancel,
  open,
  title,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
  title: string;
}) => {
  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      titleContent={<div className="font-bold text-xl">{title}</div>}
    >
      <div>All unsaved work will be lost.</div>
      <div className="flex space-x-2 justify-end">
        <SecondaryButton onClick={onCancel}>CANCEL</SecondaryButton>
        <PrimaryButton onClick={onConfirm}>CONFIRM</PrimaryButton>
      </div>
    </Modal>
  );
};

export function ClickOutConfirmCloseModal({ close }: { close: () => void }) {
  const [confirmCloseModal, setConfirmCloseModal] = useCustomOnConfirmModal();
  const [, setHasSubmitted] = useHasSubmittedMetric();

  return (
    <ConfirmModal
      title="Are you sure you want to close?"
      open={confirmCloseModal}
      onConfirm={() => {
        setConfirmCloseModal(false);
        setHasSubmitted(false);
        close();
      }}
      onCancel={() => setConfirmCloseModal(false)}
    />
  );
}

export function SwitchMeticConfirmModal() {
  const [confirmCloseModal, setConfirmCloseModal] =
    useCustomOnConfirmSwitchMetricModal();
  const { clearHistory } = useFormulaHistory();
  const [, setHasSubmitted] = useHasSubmittedMetric();
  const { customMetrics, setSelectedMetric, clearSelectedMetric } =
    useCustomMetrics();

  const { createCurrentlyUpdatingMetric } = useCurrentlyUpdatingMetric();
  const [, setIsDirty] = useSelectedIsDirtyMetric();

  const duplicateMetric = (customMetric: metrics.CustomMetric) => {
    const newMetric = {
      ...customMetric,
      id: undefined,
      name: `${customMetric.name} (copy)`,
    };
    createCurrentlyUpdatingMetric(newMetric);
    clearHistory();

    clearSelectedMetric();
    setIsDirty(true);
  };

  const onConfirm = () => {
    if (confirmCloseModal?.type === "duplicate") {
      const metricToDup = customMetrics.find(
        ({ id }) => id === confirmCloseModal.metricId,
      );
      metricToDup && duplicateMetric(metricToDup);
    } else if (confirmCloseModal?.type === "normal") {
      if (confirmCloseModal.metricId) {
        setSelectedMetric(confirmCloseModal.metricId);
      } else {
        createCurrentlyUpdatingMetric();
        clearHistory();
        clearSelectedMetric();
      }
    }
    setConfirmCloseModal(null);
    clearHistory();
    setIsDirty(false);
    setHasSubmitted(false);
  };

  return (
    <ConfirmModal
      title="Are you sure you want to switch metric?"
      open={Boolean(confirmCloseModal)}
      onConfirm={onConfirm}
      onCancel={() => setConfirmCloseModal(null)}
    />
  );
}
